import { useState, useEffect } from 'react';
import { 
  X, UserPlus, LogOut, Trash2, Shield, 
  Crown, Search, UserMinus, AlertTriangle 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { getInitials } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function GroupInfoModal({ chat, isOpen, onClose, onUpdate, onLeave }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [transferAdminOpen, setTransferAdminOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // ✅ NEU: Lokaler State für sofortiges Feedback
  const [localParticipants, setLocalParticipants] = useState([]);

  useEffect(() => {
    api.get('/auth/me').then(res => setCurrentUser(res.data));
  }, []);

  // Wenn sich der Chat ändert (z.B. beim Öffnen), Liste aktualisieren
  useEffect(() => {
    if (chat) {
      setLocalParticipants(chat.participants);
    }
  }, [chat]);

  const isAdmin = currentUser && chat.admin_ids.includes(currentUser.id);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await api.get(`/users?search=${query}`);
      // Filtern: Nur User anzeigen, die noch NICHT in der LOKALEN Liste sind
      const filtered = res.data.filter(u => 
        !localParticipants.find(p => p.id === u.id)
      );
      setSearchResults(filtered);
    } catch (error) {
      console.error(error);
    }
  };

  // ✅ Verbessert: Nimmt das ganze User-Objekt für sofortige Anzeige
  const handleAddUser = async (user) => {
    try {
      // 1. Sofort anzeigen (Optimistic Update)
      setLocalParticipants(prev => [...prev, user]);
      setSearchQuery('');
      setSearchResults([]);
      setShowAddMember(false);

      // 2. API Call senden
      await api.put(`/chats/${chat.id}`, {
        add_participants: [user.id]
      });
      
      toast.success('Nutzer hinzugefügt');
      onUpdate(); // Im Hintergrund Datenabgleich
    } catch (error) {
      // Rollback bei Fehler
      setLocalParticipants(prev => prev.filter(p => p.id !== user.id));
      toast.error('Fehler beim Hinzufügen');
    }
  };

  // ✅ Verbessert: Entfernt User sofort aus der Liste
  const handleRemoveUser = async (userId) => {
    if (!confirm('Diesen Nutzer wirklich entfernen?')) return;
    
    // Backup für Rollback
    const previousParticipants = [...localParticipants];

    try {
      // 1. Sofort aus der Anzeige entfernen
      setLocalParticipants(prev => prev.filter(p => p.id !== userId));

      // 2. API Call
      await api.put(`/chats/${chat.id}`, {
        remove_participants: [userId]
      });
      
      toast.success('Nutzer entfernt');
      onUpdate();
    } catch (error) {
      // Rollback bei Fehler
      setLocalParticipants(previousParticipants);
      toast.error('Fehler beim Entfernen');
    }
  };

  const handleLeaveGroup = async (newAdminId = null) => {
    const otherAdmins = chat.admin_ids.filter(id => id !== currentUser.id);
    const otherParticipants = localParticipants.filter(p => p.id !== currentUser.id);

    if (isAdmin && otherAdmins.length === 0 && otherParticipants.length > 0 && !newAdminId) {
      setTransferAdminOpen(true);
      return;
    }

    try {
      const url = newAdminId 
        ? `/chats/${chat.id}/leave?new_admin_id=${newAdminId}`
        : `/chats/${chat.id}/leave`;
      
      await api.post(url);
      toast.success('Gruppe verlassen');
      onClose();
      onLeave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Verlassen');
    }
  };

  const handleTerminateGroup = async () => {
    if (!confirm("Möchtest du diese Gruppe wirklich endgültig für alle löschen?")) return;
    try {
      await api.delete(`/chats/${chat.id}/terminate`);
      toast.success("Gruppe aufgelöst");
      onClose();
      onLeave();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  if (!chat || !isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Gruppeninfo: {chat.name}</DialogTitle>
            <DialogDescription className="sr-only">Mitglieder verwalten</DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-slate-500">
                  {localParticipants.length} Mitglieder
                </h3>
                {isAdmin && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowAddMember(!showAddMember)}
                    className="text-emerald-500 hover:text-emerald-600"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Hinzufügen
                  </Button>
                )}
              </div>

              {showAddMember && (
                <div className="mb-4 space-y-2 bg-slate-50 dark:bg-slate-900 p-2 rounded-lg">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Nutzer suchen..." 
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="pl-8 h-9"
                    />
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {searchResults.map(user => (
                      <div 
                        key={user.id} 
                        className="flex items-center justify-between p-2 hover:bg-white dark:hover:bg-slate-800 rounded cursor-pointer" 
                        onClick={() => handleAddUser(user)} // Hier übergeben wir jetzt das ganze Objekt
                      >
                        <span className="text-sm">{user.username}</span>
                        <UserPlus className="w-4 h-4 text-slate-400" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-3">
                  {localParticipants.map((participant) => {
                    const isParticipantAdmin = chat.admin_ids.includes(participant.id);
                    const isMe = currentUser?.id === participant.id;

                    return (
                      <div key={participant.id} className="flex items-center justify-between group">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={participant.avatar} />
                            <AvatarFallback>{getInitials(participant.username)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-1">
                              <p className="font-medium text-sm text-slate-900 dark:text-white">
                                {isMe ? 'Du' : participant.username}
                              </p>
                              {isParticipantAdmin && (
                                <Crown className="w-3 h-3 text-amber-500 fill-amber-500" />
                              )}
                            </div>
                            <p className="text-xs text-slate-500">{participant.email}</p>
                          </div>
                        </div>

                        {isAdmin && !isMe && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveUser(participant.id)}
                            className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <UserMinus className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>

            <div className="pt-4 border-t space-y-2 border-slate-100 dark:border-slate-800">
              <Button 
                variant="destructive" 
                className="w-full"
                onClick={() => handleLeaveGroup()}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Gruppe verlassen
              </Button>
              
              {isAdmin && localParticipants.length === 1 && (
                <Button 
                  variant="outline" 
                  className="w-full border-red-500 text-red-500 hover:bg-red-50"
                  onClick={handleTerminateGroup}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Gruppe endgültig auflösen
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={transferAdminOpen} onOpenChange={setTransferAdminOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Admin-Rechte übertragen
            </DialogTitle>
            <DialogDescription className="sr-only">Nachfolger wählen</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Wähle einen Nachfolger, bevor du die Gruppe verlässt.
            </p>
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {localParticipants
                  .filter(p => p.id !== currentUser?.id)
                  .map(p => (
                    <Button
                      key={p.id}
                      variant="outline"
                      className="w-full justify-between"
                      onClick={() => handleLeaveGroup(p.id)}
                    >
                      <span className="flex items-center gap-2">
                        <Avatar className="w-6 h-6">
                          <AvatarFallback className="text-xs">{getInitials(p.username)}</AvatarFallback>
                        </Avatar>
                        {p.username}
                      </span>
                      <Shield className="w-4 h-4 text-slate-400" />
                    </Button>
                  ))}
              </div>
            </ScrollArea>
            <Button variant="ghost" onClick={() => setTransferAdminOpen(false)} className="w-full">
              Abbrechen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}